//
// # Platform Interface
//
// The high-level semantic interface between the Editor and its host environment.
//
// Platform implementations provide:
// - Data access (getPdk, getStack, getContent)
// - Notifications from Editor to host (onUIChange, log, etc.)
// - A way for the host to call into the Editor (via setEditor callback)
//
// For message-based transports (WebSocket, postMessage), use MessagePlatform
// which implements this interface on top of a Transport.
//
// For in-memory hosts (like a hypothetical IDE workspace), implement Platform
// directly without message serialization overhead.
//

import { Result, msgs } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { model, layout } from "GenAlphaModel";

import type { InteractionHost } from "./interaction_host";

// Error type for Platform operations
export type PlatformError = {
  why: string;
  data?: unknown;
};

//
// # SelectionInfo
//
// Minimal payload describing the current editor selection, sent to the host
// whenever the selection changes. The host (IDE Properties Panel) uses this to
// look up full property data via `workspace.properties.describeSelection()`; we keep the
// payload minimal (entity kind + id + count) so EditorCore stays decoupled from
// the panel's view model.
//
// `id` is the entity's data id when it has one (it can be absent for transient
// entities). `count` is the total number of selected entities (>1 means a
// multi-selection).
//
export type SelectionInfo = {
  readonly entityKind: string;
  readonly id?: number;
  readonly count: number;
};

//
// # Platform Interface
//
// Editor calls these methods to request data or send notifications.
// Platform implementations handle these calls appropriately for their environment.
//
export interface Platform {
  // Platform name. For printing & debugging purposes only.
  readonly name: string;

  //
  // ## Editor Registration
  //
  // Called after Editor construction to give the Platform a reference.
  // This allows the Platform to call methods on the Editor
  // (e.g., loadMainContent, updateSettings).
  //
  setEditor(editor: InteractionHost): void;

  //
  // ## Data Access Methods
  //
  // These return Promises because they may involve async operations
  // (network requests, IPC, etc.)
  //

  // Get a PDK by ID.
  getPdk(pdkId: string): Promise<Result<schema.pdk.PDK, PlatformError>>;

  // Get a Stack from a PDK.
  getStack(
    pdkId: string,
    stackId: schema.StackId
  ): Promise<Result<schema.pdk.Stack, PlatformError>>;

  // Get content by Ident.
  getContent(
    ident: model.Ident
  ): Promise<Result<model.ContentJson, PlatformError>>;

  //
  // ## Notification Methods
  //
  // One-way notifications from Editor to Platform.
  // These don't return results - fire and forget.
  //

  // Notify that the editor has started and is ready.
  notifyEditorUp(contentType?: msgs.ContentType): void;

  // Notify that the user made a UI change.
  notifyUIChange(change: model.Change): void;

  // Notify that the editor's selection changed. Carries a minimal description
  // of the selection (or null when nothing is selected). The host derives full
  // property data separately. Fired once per selection-mutation (e.g. once at
  // the end of a box-select, not per entity).
  notifySelectionChanged(sel: SelectionInfo | null): void;

  // Notify that the user wants to edit instance parameters.
  notifyEditInstanceParameters(instanceId: string): void;

  // Notify that the user wants to add dummy devices around instances.
  // instanceNames is for dialog display only, not sent to the server.
  notifyAddDummyDevices(instanceIds: number[], instanceNames: string[]): void;

  // Notify that the user wants to add an instance at a position.
  notifyAddInstanceAtPosition(position: { x: number; y: number }): void;

  // Notify that the user wants to add a pin at a position (opens dialog).
  notifyAddPinAtPosition(position: { x: number; y: number }): void;

  // Notify that the user wants to rename a pin (opens dialog).
  notifyRenamePin(pinId: number, currentName: string): void;

  // Notify that the user wants to edit a pin's parameters (opens dialog).
  // Carries the current direction so the host can pre-populate the
  // direction selector. (Pin name is reached via notifyRenamePin.)
  notifyEditPinProperties(pinId: number, currentDir: schema.core.PortDir): void;

  // Notify that the user wants to edit a wire label (opens dialog).
  notifyEditWireLabel(wireId: number, currentLabel: string): void;

  // Notify that the user wants to add a text annotation at a position (opens dialog).
  notifyAddTextAnnotation(position: { x: number; y: number }): void;

  // Notify that the user wants to edit an existing text annotation (opens dialog).
  notifyEditTextAnnotation(elementId: number, currentText: string): void;

  // Notify that the user wants to rename an instance.
  notifyRenameInstance(instanceId: string): void;

  // Notify that the user wants to generate a layout.
  notifyGenerateFromSourceRequest(): void;

  // Notify that the user wants to open an instance's target design.
  notifyOpenInstanceTarget(target: model.Ident): void;

  // Notify that the user wants to reveal an instance's target in the Depot tree.
  notifyViewInDepot(target: model.Ident): void;

  // Notify that the user wants to reveal the current design in the Depot tree.
  notifyViewCurrentDesignInDepot(): void;

  // Notify that the user wants to add a symbol pin (opens dialog for configuration).
  notifyAddSymbolPin(position: { x: number; y: number }): void;

  // Notify that the user wants to edit an existing symbol pin's properties.
  notifyEditSymbolPin(pinData: {
    portName: string;
    width: number;
    dir: schema.core.PortDir;
    labelPosition: string;
    id?: number;
  }): void;

  // Notify that the user wants to add a layout pin (opens dialog for configuration).
  notifyAddLayoutPin(position: { x: number; y: number }): void;

  // Notify that the user wants to edit an existing layout pin's properties.
  notifyEditLayoutPin(pinData: {
    name: string;
    layer: number;
    pinType: schema.core.PinType | undefined;
    id: number;
  }): void;

  // Notify that the user wants to add an expression label (opens dialog for configuration).
  notifyAddExprLabel(position: { x: number; y: number }): void;

  // Notify that the user wants to edit an existing expression label.
  notifyEditExprLabel(labelData: {
    kind: schema.symbol.ExprKind;
    name?: string;
    paramName?: string;
    id: number;
  }): void;

  // Notify that the user wants to open the graphics catalogue dialog.
  notifyOpenGraphicsCatalogue(): void;

  // Notify that the user wants to configure power routing (opens dialog).
  notifyConfigurePowerRouting(): void;

  // Notify that the user wants to edit a route width (opens the width dialog).
  // Called from DrawRoute (W key, no routeId) or the route context menu (with a
  // routeId for an existing route). Optional so non-IDE platforms can skip it.
  notifyEditRouteWidth?(params: layout.EditRouteWidthParams): void;

  // Notify that the user wants to create a layout from the current schematic.
  notifyGenerateFromSource(): void;

  // Notify that the user wants to reveal an instance in the Hierarchy panel.
  notifyShowInHierarchy(instanceName: string, instanceId: number): void;

  // Notify that the user wants to save the current design under a new name.
  notifySaveAsRequested(): void;

  // Whether this design was opened from another design (has a parent breadcrumb).
  hasParentBreadcrumb(): boolean;

  // Notify that the user wants to navigate back to the parent design.
  notifyOpenParent(): void;

  // Notify that the user wants to open the schematic(s) implementing the current
  // symbol design. Navigates directly when there is a single linked schematic,
  // otherwise the host presents a picker.
  notifyOpenLinkedSchematic(): void;

  // Notify that the user wants to open the symbol that the current schematic
  // design implements (the schematic's `symbolImpl` back-reference).
  notifyOpenLinkedSymbol(): void;

  // Notify that the user wants to open the schematic(s) implementing a symbol
  // instance's target symbol. The resolved symbol is already in memory on the
  // instance, so its `schematicImpls` are passed directly to avoid a re-fetch.
  notifyOpenSchematicForInstance(
    schematicImpls: ReadonlyArray<schema.symbol.SchematicImplRef>,
    pdkId?: string
  ): void;

  // Send the main content to the platform (for saving or other purposes).
  sendMainContent(content: schema.Content): void;

  // Send a response to a GetMainContentRequest.
  sendMainContentResponse(uuid: string, content: schema.Content): void;

  // Send a response to an UpdateMainContentRequest.
  sendUpdateMainContentResponse(
    uuid: string,
    result: Result<void, unknown>
  ): void;

  // Send a response to a RefreshMainContentRequest.
  sendRefreshMainContentResponse(
    uuid: string,
    result: Result<void, unknown>
  ): void;

  // Send a response to a CaptureCanvasRequest.
  sendCaptureCanvasResponse(uuid: string, result: Result<string, string>): void;

  // Logging methods
  log(body: unknown): void;
  warn(body: unknown): void;
  error(body: unknown): void;
}

//
// # NullPlatform
//
// A do-nothing Platform implementation.
// Useful during startup before a real Platform is available,
// or for testing.
//
export const NullPlatform: Platform = {
  name: "NullPlatform",

  setEditor(_editor: InteractionHost): void {},

  async getPdk(_pdkId: string): Promise<Result<schema.pdk.PDK, PlatformError>> {
    return Result.Err({ why: "null-platform" });
  },

  async getStack(
    _pdkId: string,
    _stackId: schema.StackId
  ): Promise<Result<schema.pdk.Stack, PlatformError>> {
    return Result.Err({ why: "null-platform" });
  },

  async getContent(
    _ident: model.Ident
  ): Promise<Result<model.ContentJson, PlatformError>> {
    return Result.Err({ why: "null-platform" });
  },

  notifyEditorUp(_contentType?: msgs.ContentType): void {},
  notifyUIChange(_change: model.Change): void {},
  notifySelectionChanged(_sel: SelectionInfo | null): void {},
  notifyEditInstanceParameters(_instanceId: string): void {},
  notifyAddDummyDevices(
    _instanceIds: number[],
    _instanceNames: string[]
  ): void {},
  notifyAddInstanceAtPosition(_position: { x: number; y: number }): void {},
  notifyAddPinAtPosition(_position: { x: number; y: number }): void {},
  notifyRenamePin(_pinId: number, _currentName: string): void {},
  notifyEditPinProperties(
    _pinId: number,
    _currentDir: schema.core.PortDir
  ): void {},
  notifyEditWireLabel(wireId: number, currentLabel: string): void {
    void wireId;
    void currentLabel;
  },
  notifyAddTextAnnotation(_position: { x: number; y: number }): void {},
  notifyEditTextAnnotation(elementId: number, currentText: string): void {
    void elementId;
    void currentText;
  },
  notifyRenameInstance(_instanceId: string): void {},

  notifyGenerateFromSourceRequest(): void {},
  notifyOpenInstanceTarget(_target: model.Ident): void {},
  notifyViewInDepot(_target: model.Ident): void {},
  notifyViewCurrentDesignInDepot(): void {},
  notifyAddSymbolPin(_position: { x: number; y: number }): void {},
  notifyEditSymbolPin(_pinData: {
    portName: string;
    width: number;
    dir: schema.core.PortDir;
    labelPosition: string;
    id?: number;
  }): void {},
  notifyAddLayoutPin(_position: { x: number; y: number }): void {},
  notifyEditLayoutPin(_pinData: {
    name: string;
    layer: number;
    pinType: schema.core.PinType | undefined;
    id: number;
  }): void {},
  notifyAddExprLabel(_position: { x: number; y: number }): void {},
  notifyEditExprLabel(labelData: {
    kind: schema.symbol.ExprKind;
    name?: string;
    paramName?: string;
    id: number;
  }): void {
    void labelData;
  },
  notifyOpenGraphicsCatalogue(): void {},
  notifyConfigurePowerRouting(): void {},
  notifyGenerateFromSource(): void {},
  notifyShowInHierarchy(_instanceName: string, _instanceId: number): void {},
  notifySaveAsRequested(): void {},
  hasParentBreadcrumb(): boolean {
    return false;
  },
  notifyOpenParent(): void {},
  notifyOpenLinkedSchematic(): void {},
  notifyOpenLinkedSymbol(): void {},
  notifyOpenSchematicForInstance(
    _schematicImpls: ReadonlyArray<schema.symbol.SchematicImplRef>,
    _pdkId?: string
  ): void {},
  sendMainContent(_content: schema.Content): void {},
  sendMainContentResponse(_uuid: string, _content: schema.Content): void {},
  sendUpdateMainContentResponse(
    _uuid: string,
    _result: Result<void, unknown>
  ): void {},
  sendRefreshMainContentResponse(
    _uuid: string,
    _result: Result<void, unknown>
  ): void {},
  sendCaptureCanvasResponse(
    _uuid: string,
    _result: Result<string, string>
  ): void {},
  log(_body: unknown): void {},
  warn(_body: unknown): void {},
  error(_body: unknown): void {},
};
